import { useEffect, useState } from "react";
import { http } from "../api";
import { Building2, Users, Pencil, Eye, EyeOff, Trash2, UserPlus, UserMinus, Search, BellRing } from "lucide-react";
import { Modal, PageHeader, ImportExportBar, AddButton, FormRow, EmptyState, PersonLink } from "../shared";

const empty = { name: "", org_type: "Club", address: "", ward: 7, established: "", registration_no: "", contact_person: "", contact_mobile: "", member_count: 0, relationship: "Neutral", notes: "" };
const MEMBER_ROLES = ["President", "Vice President", "Secretary", "Treasurer", "Coordinator", "Member", "Volunteer"];

export default function Organizations({ user }) {
  const isAdmin = user && (user.role === "Super Admin" || user.role === "Admin");
  const [rows, setRows] = useState([]);
  const [showHidden, setShowHidden] = useState(false);

  // Create/edit modal
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  // Members drawer
  const [manageOrg, setManageOrg] = useState(null);
  const [members, setMembers] = useState([]);
  const [memberBusy, setMemberBusy] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [pickRole, setPickRole] = useState("Member");
  const [pickTag, setPickTag] = useState("");
  const [notify, setNotify] = useState(true);
  const [addStatus, setAddStatus] = useState("");

  const load = () => http.get(`/organizations${showHidden ? "?include_hidden=true" : ""}`).then((r) => setRows(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showHidden]);

  const openNew = () => { setEditing(null); setForm(empty); setShow(true); };
  const openEdit = (o) => { setEditing(o); setForm({ ...empty, ...o }); setShow(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, ward: Number(form.ward) || 7, member_count: Number(form.member_count) || 0 };
      if (editing) await http.patch(`/organizations/${editing.organization_id}`, payload);
      else await http.post("/organizations", payload);
      setShow(false); setEditing(null); setForm(empty); load();
    } finally { setSaving(false); }
  };

  const toggleHide = async (o) => {
    await http.patch(`/organizations/${o.organization_id}`, { hidden: !o.hidden });
    load();
  };

  const removeOrg = async (o) => {
    if (!window.confirm(`Delete "${o.name}"? This cannot be undone.`)) return;
    await http.delete(`/organizations/${o.organization_id}`);
    load();
  };

  const openMembers = async (o) => {
    setManageOrg(o); setSearchQ(""); setCandidates([]); setAddStatus("");
    setMemberBusy(true);
    try {
      const r = await http.get(`/organizations/${o.organization_id}/members`);
      setMembers(r.data);
    } finally { setMemberBusy(false); }
  };

  useEffect(() => {
    if (!searchQ || searchQ.length < 2) { setCandidates([]); return; }
    const t = setTimeout(() => {
      http.get(`/contacts?q=${encodeURIComponent(searchQ)}`).then((r) => setCandidates(r.data.slice(0, 8)));
    }, 250);
    return () => clearTimeout(t);
  }, [searchQ]);

  const addMember = async (c) => {
    setMemberBusy(true); setAddStatus("");
    try {
      await http.post(`/organizations/${manageOrg.organization_id}/members`, {
        contact_id: c.contact_id, role: pickRole, tag: pickTag, notify,
      });
      const r = await http.get(`/organizations/${manageOrg.organization_id}/members`);
      setMembers(r.data); setCandidates([]); setSearchQ("");
      setAddStatus(`${c.name} added as ${pickRole}${notify ? " · notification sent" : ""}`);
      load();
    } catch (e) {
      setAddStatus(e.response?.data?.detail || "Failed to add member");
    } finally { setMemberBusy(false); }
  };

  const removeMember = async (m) => {
    if (!window.confirm(`Remove ${m.name || m.contact_id} from ${manageOrg.name}?`)) return;
    setMemberBusy(true);
    try {
      await http.delete(`/organizations/${manageOrg.organization_id}/members/${m.contact_id}`);
      const r = await http.get(`/organizations/${manageOrg.organization_id}/members`);
      setMembers(r.data);
      load();
    } finally { setMemberBusy(false); }
  };

  return (
    <>
      <PageHeader eyebrow="COMMUNITY NETWORK" title="Organizations" subtitle={`${rows.length} clubs, NGOs and associations${!isAdmin ? " · read-only" : ""}`}
        actions={<>
          {isAdmin && (
            <button className="outline" data-testid="orgs-toggle-hidden" onClick={() => setShowHidden((v) => !v)}>
              {showHidden ? <EyeOff size={14} /> : <Eye size={14} />} {showHidden ? "Hide hidden" : "Show hidden"}
            </button>
          )}
          {isAdmin && <ImportExportBar entity="organizations" onImported={load} />}
          {isAdmin && <AddButton onClick={openNew} label="Add organization" testid="orgs-add-button" />}
        </>} />

      {!isAdmin && (
        <div className="notice-strip" data-testid="orgs-readonly-notice">
          <Building2 size={14} /> You have read-only access. Ask an admin to add or edit organizations.
        </div>
      )}

      <section className="panel">
        {rows.length === 0 ? <EmptyState icon={Building2} title="No organizations" hint="Add a club/NGO to begin." /> : (
          <div className="grid-cards">
            {rows.map((o, i) => (
              <div key={o.organization_id} className={`org-card ${o.hidden ? "org-hidden" : ""}`} data-testid={`org-card-${i}`} style={o.hidden ? { opacity: 0.55, borderStyle: "dashed" } : {}}>
                <div className="org-head">
                  <Building2 size={18} /><b>{o.name}</b>
                  <span className={`chip ${o.relationship === "Supporter" ? "green" : o.relationship === "Opposition" ? "red" : "blue"}`}>{o.relationship}</span>
                  {o.hidden && <span className="chip amber" data-testid={`org-hidden-badge-${i}`}>Hidden</span>}
                </div>
                <p className="hint">{o.org_type} · Est. {o.established || "—"} · Ward {o.ward}</p>
                <p>{o.address}</p>
                <div className="org-meta">
                  <span><Users size={13} /> {(() => { const n = o.member_count || (o.members || []).length; return `${n} member${n === 1 ? "" : "s"}`; })()}</span>
                  <span>Contact: <b>{o.contact_person}</b> {o.contact_mobile}</span>
                </div>
                {o.notes && <p className="notes">{o.notes}</p>}

                <div className="row-actions" style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
                  <button className="link" data-testid={`org-members-${i}`} onClick={() => openMembers(o)}>
                    <UserPlus size={12} /> {isAdmin ? "Manage members" : "View members"}
                  </button>
                  {isAdmin && (
                    <>
                      <button className="link" data-testid={`org-edit-${i}`} onClick={() => openEdit(o)}>
                        <Pencil size={12} /> Edit
                      </button>
                      <button className="link" data-testid={`org-hide-${i}`} onClick={() => toggleHide(o)}>
                        {o.hidden ? <><Eye size={12} /> Unhide</> : <><EyeOff size={12} /> Hide</>}
                      </button>
                      <button className="link danger" data-testid={`org-delete-${i}`} onClick={() => removeOrg(o)}>
                        <Trash2 size={12} /> Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {show && (
        <Modal title={editing ? `Edit ${editing.name}` : "Add organization"} onClose={() => { setShow(false); setEditing(null); }} testid="org-modal" isDirty={() => JSON.stringify(form) !== JSON.stringify(editing ? { ...empty, ...editing } : empty)}>
          <div className="form-grid">
            <FormRow label="Name"><input data-testid="form-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></FormRow>
            <FormRow label="Type">
              <select data-testid="form-type-input" value={form.org_type} onChange={(e) => setForm({ ...form, org_type: e.target.value })}>
                <option>Club</option><option>NGO</option><option>Association</option><option>Trust</option><option>Cooperative</option>
              </select>
            </FormRow>
            <FormRow label="Established"><input data-testid="form-established-input" value={form.established} onChange={(e) => setForm({ ...form, established: e.target.value })} placeholder="Year" /></FormRow>
            <FormRow label="Registration No"><input data-testid="form-reg-input" value={form.registration_no} onChange={(e) => setForm({ ...form, registration_no: e.target.value })} /></FormRow>
            <FormRow label="Contact person"><input data-testid="form-contact-input" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></FormRow>
            <FormRow label="Contact mobile"><input data-testid="form-mobile-input" value={form.contact_mobile} onChange={(e) => setForm({ ...form, contact_mobile: e.target.value })} /></FormRow>
            <FormRow label="Member count"><input data-testid="form-members-input" type="number" value={form.member_count} onChange={(e) => setForm({ ...form, member_count: e.target.value })} /></FormRow>
            <FormRow label="Relationship">
              <select data-testid="form-relationship-input" value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })}>
                <option>Supporter</option><option>Neutral</option><option>Opposition</option>
              </select>
            </FormRow>
          </div>
          <FormRow label="Address"><input data-testid="form-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></FormRow>
          <FormRow label="Notes"><input data-testid="form-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></FormRow>
          <div className="form-actions">
            <button className="outline" data-testid="form-cancel-button" onClick={() => { setShow(false); setEditing(null); }}>Cancel</button>
            <button className="primary" data-testid="form-save-button" disabled={saving || !form.name} onClick={save}>{saving ? "Saving…" : editing ? "Save changes" : "Create"}</button>
          </div>
        </Modal>
      )}

      {manageOrg && (
        <Modal title={`${manageOrg.name} · Members`} onClose={() => setManageOrg(null)} testid="org-members-modal">
          {isAdmin && (
            <div style={{ background: "#f7fbf8", border: "1px solid var(--line)", borderRadius: 12, padding: 12, marginBottom: 12 }}>
              <p className="eyebrow" style={{ margin: 0 }}>ADD MEMBER</p>
              <div style={{ position: "relative", marginTop: 8 }}>
                <span style={{ position: "absolute", left: 10, top: 10, opacity: 0.6 }}><Search size={14} /></span>
                <input data-testid="member-search-input" placeholder="Search person by name, mobile or EPIC…" value={searchQ} onChange={(e) => setSearchQ(e.target.value)} style={{ width: "100%", paddingLeft: 30 }} />
                {candidates.length > 0 && (
                  <div className="people-table" style={{ marginTop: 8, background: "#fff", border: "1px solid var(--line)", borderRadius: 10, maxHeight: 200, overflowY: "auto" }} data-testid="member-candidates">
                    {candidates.map((c, i) => (
                      <div key={c.contact_id} className="tr" data-testid={`member-candidate-${i}`} style={{ padding: "8px 10px" }}>
                        <span className="person">
                          <span className="avatar person-avatar">{c.name.split(" ").map((x) => x[0]).join("")}</span>
                          <span><b>{c.name}</b><small>{c.gender} · Booth {c.booth} · {c.mobile}</small></span>
                        </span>
                        <button className="primary" data-testid={`member-add-${i}`} disabled={memberBusy} onClick={() => addMember(c)} style={{ padding: "6px 12px" }}>
                          <UserPlus size={12} /> Add
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="form-grid" style={{ marginTop: 8 }}>
                <FormRow label="Role">
                  <select data-testid="member-role-select" value={pickRole} onChange={(e) => setPickRole(e.target.value)}>
                    {MEMBER_ROLES.map((r) => <option key={r}>{r}</option>)}
                  </select>
                </FormRow>
                <FormRow label="Tag (optional)"><input data-testid="member-tag-input" value={pickTag} onChange={(e) => setPickTag(e.target.value)} placeholder="e.g. Youth wing" /></FormRow>
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4, cursor: "pointer" }} data-testid="member-notify-toggle">
                <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
                <BellRing size={13} /> Notify the person in-app about this responsibility
              </label>
              {addStatus && <small className="ai-hint" data-testid="member-add-status" style={{ display: "block", marginTop: 8 }}>{addStatus}</small>}
            </div>
          )}

          <h4 style={{ fontSize: 12 }}>CURRENT MEMBERS ({members.length})</h4>
          {members.length === 0 ? (
            <EmptyState icon={Users} title="No members yet" hint={isAdmin ? "Search above and tap Add." : "Ask an admin to tag members."} />
          ) : (
            <div className="people-table" data-testid="member-list">
              <div className="tr th"><span>Person</span><span>Role / Tag</span><span>Added</span><span></span></div>
              {members.map((m, i) => (
                <div key={m.contact_id} className="tr" data-testid={`member-row-${i}`}>
                  <span className="person">
                    <span className="avatar person-avatar">{(m.name || m.contact?.name || "?").split(" ").map((x) => x[0]).join("")}</span>
                    <span>
                      <PersonLink contactId={m.contact_id || m.contact?.contact_id} name={m.name || m.contact?.name} testid={`org-member-link-${m.contact_id || m.contact?.contact_id}`} className="person-link-name" />
                      <small>{m.contact?.mobile || "no mobile"} · Booth {m.contact?.booth || "—"}</small>
                    </span>
                  </span>
                  <span><b>{m.role}</b>{m.tag ? <><br /><small>{m.tag}</small></> : null}</span>
                  <span><small>{m.added_at ? new Date(m.added_at).toLocaleDateString() : "—"}<br />by {m.added_by || "—"}</small></span>
                  <span>
                    {isAdmin && (
                      <button className="link danger" data-testid={`member-remove-${i}`} onClick={() => removeMember(m)}>
                        <UserMinus size={12} /> Remove
                      </button>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}
    </>
  );
}
